import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import CreatorStudioLayout from "./CreatorStudioLayout";
import { loadCreatorStudioData } from "../../services/creatorStudioService";
import { startCreatorConnectOnboarding } from "../../services/settingsService";
import {
  computeMonthlyEarnings,
  contentTypeLabel,
  dropEarned,
  dropProgress,
  formatMoney,
  formatMoneyWhole,
  getDropStatus,
  greetingName,
  nextPayoutDate,
} from "./creatorHelpers";
import SessionDismissible, { NUDGE_IDS } from "../../components/nudges/SessionDismissible";
import { useReferralOverlay } from "../../context/ReferralOverlayContext";
import "./DashboardPayoutNudge.css";

const PERIODS = ["7 days", "This month", "Last month", "This year", "All time"];

export default function Dashboard() {
  const { currentUser, userProfile } = useAuth();
  const { openReferral } = useReferralOverlay();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("This month");
  const [connectBusy, setConnectBusy] = useState(false);
  const [payoutToast, setPayoutToast] = useState("");

  useEffect(() => {
    if (!currentUser?.uid) return;
    setLoading(true);
    loadCreatorStudioData(currentUser.uid)
      .then(setData)
      .finally(() => setLoading(false));
  }, [currentUser?.uid]);

  const { period: greetPeriod, name } = greetingName(userProfile?.displayName);
  const monthOffset = period === "Last month" ? 1 : 0;
  const monthStats = useMemo(
    () => (data?.drops ? computeMonthlyEarnings(data.drops, userProfile, monthOffset) : { net: 0, gross: 0 }),
    [data?.drops, userProfile, monthOffset]
  );

  const recentDrops = (data?.drops || []).slice(0, 5);
  const liveViews = data?.stats?.liveViews || 0;

  const earningsBalance = useMemo(() => {
    const drops = data?.drops || [];
    return drops.reduce((sum, d) => sum + dropEarned(d, userProfile), 0);
  }, [data?.drops, userProfile]);

  const needsPayoutSetup =
    earningsBalance > 0 && !userProfile?.stripe?.connectAccountId;

  useEffect(() => {
    if (searchParams.get("payout") === "return") {
      setPayoutToast(
        userProfile?.stripe?.connectAccountId || userProfile?.payoutSetupComplete
          ? "Payout setup complete — you're ready to get paid."
          : "Thanks — Stripe is verifying your account. This usually finishes in a few minutes."
      );
      const t = setTimeout(() => setPayoutToast(""), 5000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [searchParams, userProfile?.stripe?.connectAccountId, userProfile?.payoutSetupComplete]);

  const handleSetupPayouts = async () => {
    try {
      setConnectBusy(true);
      await startCreatorConnectOnboarding();
    } catch (err) {
      setPayoutToast(err?.message || "Could not open Stripe setup.");
      setConnectBusy(false);
    }
  };

  const monthlyBars = useMemo(() => {
    if (!data?.drops?.length) return [];
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const label = d.toLocaleDateString("en-CA", { month: "short" });
      const { gross } = computeMonthlyEarnings(data.drops, userProfile, now.getMonth() - d.getMonth());
      months.push({ label, gross });
    }
    const max = Math.max(...months.map((m) => m.gross), 1);
    return months.map((m) => ({ ...m, pct: Math.round((m.gross / max) * 100) }));
  }, [data?.drops, userProfile]);

  if (loading) {
    return (
      <CreatorStudioLayout>
        <div className="cs-loading">Loading dashboard…</div>
      </CreatorStudioLayout>
    );
  }

  return (
    <CreatorStudioLayout>
      <div className="cs-dash-header">
        <div>
          <div className="cs-dash-greeting">{greetPeriod}, <em>{name}.</em></div>
          <span className="cs-dash-hand">
            {data?.stats?.openCount
              ? `your window is open — ${liveViews} views across live drops`
              : "post a drop to open the window"}
          </span>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="cs-btn" onClick={openReferral}>Referral tools</button>
          <Link to="/broadcasts" className="cs-btn">Send broadcast</Link>
          <Link to="/post" className="cs-btn cs-btn-dark">+ New Drop</Link>
        </div>
      </div>

      {needsPayoutSetup && (
        <SessionDismissible id={NUDGE_IDS.DASH_PAYOUT}>
        <div className="cs-payout-nudge">
          <div className="cs-pn-icon" aria-hidden>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <line x1="2" y1="10" x2="22" y2="10" />
            </svg>
          </div>
          <div className="cs-pn-body">
            <div className="cs-pn-title">You&apos;ve got earnings waiting to be paid out</div>
            <div className="cs-pn-sub">
              You have <strong>{formatMoney(earningsBalance)}</strong> sitting uncollected — set up
              payouts to receive it on the next payout date.
            </div>
          </div>
          <button
            type="button"
            className="cs-pn-btn"
            onClick={handleSetupPayouts}
            disabled={connectBusy}
          >
            {connectBusy ? "Opening…" : "Set Up Payouts"}
          </button>
        </div>
        </SessionDismissible>
      )}

      {payoutToast && (
        <div className="cs-payout-toast" role="status">{payoutToast}</div>
      )}

      <div className="cs-period-tabs">
        {PERIODS.map((p) => (
          <button key={p} type="button" className={`cs-period-tab${period === p ? " active" : ""}`} onClick={() => setPeriod(p)}>
            {p}
          </button>
        ))}
      </div>

      <div className="cs-stats-row">
        <div className="cs-stat-card highlight">
          <div className="cs-stat-label light">Earnings this month</div>
          <div className="cs-stat-num gold">{formatMoneyWhole(monthStats.net)}</div>
          <div className="cs-stat-change up">after 10% platform fee</div>
          <span className="cs-stat-hand light">founding creator rate</span>
        </div>
        <div className="cs-stat-card">
          <div className="cs-stat-label">Subscribers</div>
          <div className="cs-stat-num">{data?.stats?.subscriberCount || userProfile?.supporterCount || 0}</div>
          <span className="cs-stat-hand">tier supporters</span>
        </div>
        <div className="cs-stat-card">
          <div className="cs-stat-label">Drops</div>
          <div className="cs-stat-num">{data?.stats?.totalDrops || 0}</div>
          <div className="cs-stat-change" style={{ color: "var(--text-muted)" }}>
            {data?.stats?.openCount || 0} currently live
          </div>
          <span className="cs-stat-hand">{data?.stats?.draftCount || 0} drafts</span>
        </div>
        <div className="cs-stat-card">
          <div className="cs-stat-label">Live views</div>
          <div className="cs-stat-num">{liveViews}</div>
          <span className="cs-stat-hand">across open drops</span>
        </div>
      </div>

      <div className="cs-card" style={{ marginBottom: 20 }}>
        <div className="cs-card-head">
          <div>
            <div className="cs-table-title">Monthly <em>earnings</em></div>
            <span className="cs-section-hand" style={{ marginBottom: 0 }}>from paid drop unlocks</span>
          </div>
          <div className="cs-stat-num" style={{ fontSize: 32 }}>{formatMoney(monthStats.gross)}</div>
        </div>
        <div className="cs-bar-chart">
          {monthlyBars.map((bar) => (
            <div key={bar.label} className="cs-bar-col">
              <div className={`cs-bar niche${bar.pct > 80 ? "" : ""}`} style={{ height: `${Math.max(bar.pct, 4)}%` }} />
              <span className="cs-bar-lbl">{bar.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="cs-grid-2">
        <div className="cs-table-card">
          <div className="cs-table-head">
            <div>
              <div className="cs-table-title">Recent <em>drops</em></div>
            </div>
            <Link to="/my-drops" className="cs-table-link">View all →</Link>
          </div>
          {recentDrops.length === 0 ? (
            <div className="cs-empty">No drops yet. <Link to="/post">Post your first drop</Link></div>
          ) : (
            <table className="cs-table">
              <thead>
                <tr>
                  <th>Drop</th>
                  <th>Status</th>
                  <th>Views</th>
                  <th>Earned</th>
                </tr>
              </thead>
              <tbody>
                {recentDrops.map((drop) => {
                  const status = getDropStatus(drop);
                  const views = Number(drop.viewCount || 0);
                  return (
                    <tr key={drop.id}>
                      <td>
                        <div style={{ fontWeight: 600, color: "var(--text)", marginBottom: 2 }}>{drop.title || "Untitled"}</div>
                        <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{contentTypeLabel(drop.contentType)}</div>
                      </td>
                      <td>
                        <span className={`cs-td-pill cs-td-pill-${status === "open" ? "open" : "closed"}`}>
                          {status === "draft" ? "Draft" : status === "open" ? "Open" : "Closed"}
                        </span>
                      </td>
                      <td>
                        <div className="cs-td-scar">
                          <div className="cs-td-scar-bar">
                            <div className="cs-td-scar-fill" style={{ width: `${dropProgress(drop)}%` }} />
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 600 }}>{views}/100</span>
                        </div>
                      </td>
                      <td className="cs-td-earn">{formatMoney(dropEarned(drop, userProfile))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="cs-payout-card-dark">
          <div style={{ position: "relative", zIndex: 1 }}>
            <div className="cs-stat-label light" style={{ marginBottom: 6 }}>Next payout</div>
            <div className="cs-stat-num light" style={{ fontSize: 48, marginBottom: 4 }}>{formatMoneyWhole(monthStats.net)}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 14 }}>Scheduled {nextPayoutDate()}</div>
            <span className="cs-stat-hand light">via Stripe Connect 🍁</span>
            <div style={{ height: 1, background: "rgba(255,255,255,0.08)", margin: "14px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "rgba(255,255,255,0.45)" }}>
              <span>Gross this month</span>
              <span style={{ fontWeight: 600, color: "rgba(255,255,255,0.7)" }}>{formatMoney(monthStats.gross)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 8 }}>
              <span>Platform fee</span>
              <span style={{ color: "#f08090" }}>−{formatMoney(monthStats.platformFee || monthStats.gross * 0.1)}</span>
            </div>
            <Link to="/earnings" className="cs-btn cs-btn-primary" style={{ width: "100%", justifyContent: "center", marginTop: 16 }}>
              View earnings
            </Link>
          </div>
        </div>
      </div>
    </CreatorStudioLayout>
  );
}
